import { Routes, Route, useLocation } from "react-router-dom";
import React, { useEffect, useState, Suspense, lazy } from "react";
import requestPermission, { handleForegroundMessages } from "./notification";
import { trackClick } from "./utils/clickTracking";
import { trackFunnel, getDeviceId } from "./utils/funnel";
import { trackEvent } from "./analytics";
import "react-tooltip/dist/react-tooltip.css";
import { Toaster } from "react-hot-toast";
import AnimatedCursor from "react-animated-cursor";
import "./App.css";
import BVNDetails from "./pages/Account and Profile/BvnDetails";
import UpdatePassword from "./pages/Account and Profile/UpdatePassword";
import LoginPin from "./pages/Auth/Login/LoginPin.jsx";
const Login = lazy(() => import("./pages/Auth/Login/Login"));

const UpdatePhone = lazy(() => import("./pages/Auth/UpdatePhone/UpdatePhone"));
const PublicCliqs = lazy(() => import("./pages/CliqSavings/PublicCliqs"));

const SignUp = lazy(() => import("./pages/Auth/SignUp/SignUp"));
const SignupOtp = lazy(() => import("./pages/Auth/SignUp/SignupOtp"));
const Welcome = lazy(() => import("./pages/Auth/SignUp/Welcome"));
const SessionExpired = lazy(() =>
  import("./pages/Auth/SessionExpired/SessionExpired")
);

const VerifyEmailV2 = lazy(() =>
  import("./pages/Auth/VerifyEmailV2/VerifyEmail.jsx")
);
const VerifyPhone = lazy(() => import("./pages/Auth/VerifyPhone/VerifyPhone"));
const SetTransactionPin = lazy(() =>
  import("./pages/Auth/SetPin/SetTransactionPin")
);
const NotFound = lazy(() => import("./pages/404/NotFound"));
const ForgotPassword = lazy(() =>
  import("./pages/Auth/ForgotPassword/ForgotPassword")
);
const ResetPassword = lazy(() =>
  import("./pages/Auth/ForgotPassword/ResetPassword")
);
const ResetPassword2 = lazy(() =>
  import("./pages/Auth/ForgotPassword/ResetPassword2")
);
const LoginModal = lazy(() => import("./pages/Auth/Login/LoginModal"));
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));
const BVNSuccessModal = lazy(() =>
  import("./pages/AccountSecurity/BVNSuccessModal")
);
const Plans = lazy(() => import("./pages/Plans/Plans"));
const CreatePlan = lazy(() => import("./pages/Plans/CreatePlan"));
const PlanSummary = lazy(() => import("./pages/Plans/PlanSummary"));
const PlansPayment = lazy(() => import("./pages/Plans/PlansPayment"));
const PlansSuccess = lazy(() => import("./pages/Plans/PlanSuccess"));
const PersonalSavings = lazy(() => import("./pages/Plans/PersonalSavings"));
const Purse = lazy(() => import("./pages/Purse/Purse"));
const Investments = lazy(() => import("./pages/Investments/Investments"));
const Agro = lazy(() => import("./pages/Investments/Agro/Agro"));
const Dollar = lazy(() => import("./pages/Investments/Dollar/Dollar"));
const MyWIT = lazy(() => import("./pages/Investments/WIT/MyWIT"));
const MyAgroInvestments = lazy(() =>
  import("./pages/Investments/Agro/MyAgroInvestments")
);
const NewWIT = lazy(() => import("./pages/Investments/WIT/NewWIT"));
const Bank = lazy(() => import("./pages/Banks/Bank"));
const Cards = lazy(() => import("./pages/Cards/Cards"));
const Referral = lazy(() => import("./pages/Referrals/Referral"));
const Invites = lazy(() => import("./pages/Referrals/Invites"));
const Portfolio = lazy(() =>
  import("./pages/Portfolio and Transactions/Portfolio")
);
const Transactions = lazy(() =>
  import("./pages/Portfolio and Transactions/Transactions")
);
const AboutHerVest = lazy(() => import("./pages/AboutHervest/AboutHerVest"));
const Profile = lazy(() => import("./pages/Account and Profile/Profile"));
const NOK = lazy(() => import("./pages/Account and Profile/NOK"));
const ChangePassword = lazy(() =>
  import("./pages/Account and Profile/UpdatePassword")
);
const Locked = lazy(() => import("./pages/LockedSavings/Locked"));
const AirtimeandBills = lazy(() =>
  import("./pages/Airtime and Bills/AirtimeandBills")
);
const Leaderboard = lazy(() => import("./pages/Leaderboard/Leaderboard"));
const Support = lazy(() => import("./pages/Support/Support"));
const Cliq = lazy(() => import("./pages/CliqSavings/Cliq"));
const ValidatePayment = lazy(() =>
  import("./pages/ValidatePayment/ValidatePayment")
);

const TopupValidate = lazy(() => import("./pages/TopupValidate/TopupValidate"));
const VerifyLoanMandate = lazy(() => import("./pages/Loan/VerifyLoanMandate"));
const Apps = lazy(() => import("./pages/Apps/Apps"));
const RegisterDeepLink = lazy(() => import("./pages/Apps/RegisterDeepLink"));
const CliqDeepLink = lazy(() => import("./pages/Apps/CliqDeepLink"));
const MarketingDeepLink = lazy(() => import("./pages/Apps/MarketingDeepLink"));
const RegisterCliq = lazy(() => import("./pages/Apps/RegisterCliq"));

const SplashScreen = lazy(() =>
  import("./shared-components/SplashScreen/SplashScreen")
);

// const LoadingPageSplash = lazy(() =>
//   import("./shared-components/SplashScreen/LoadingPageSplash")
// );//

const NewDeviceDetected = lazy(() =>
  import("./pages/Auth/NewDeviceDetected/NewDevice")
);

const Loan = lazy(() => import("./pages/Loan/Loan"));

// Fires one click-tracking hit per route, on landing and every navigation
// after - the page-by-page journey behind the admin "Clicks" analytics page.
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    trackClick(location.pathname);
    trackFunnel("page_view", { route: location.pathname });
    // Named milestones the admin journey view and funnel report key off.
    if (location.pathname === "/dashboard") trackFunnel("dashboard_view");
    if (location.pathname === "/onboarding") trackFunnel("onboarding_view");
    if (location.pathname === "/welcome") trackFunnel("welcome_view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // Mint the device ID on first paint so it exists before any signup call.
  useEffect(() => {
    getDeviceId();
    trackEvent("splash_screen_viewed");
  }, []);

  return null;
}

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    requestPermission();
    handleForegroundMessages();
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);

  return (
    <>
      <RouteTracker />

      {/* CURSOR IMPLEMENTATION STARTS HERE */}
      <AnimatedCursor
        innerSize={8}
        outerSize={8}
        color="193, 11, 111"
        outerAlpha={0.2}
        innerScale={0.7}
        outerScale={5}
        clickables={[
          "a",
          "input",
          "label[for]",
          "select",
          "textarea",
          "button",
          ".link",
        ]}
        outerStyle={{
          mixBlendMode: "exclusion",
        }}
      />
      {/* CURSOR IMPLEMENTATION ENDS HERE */}

      {/* ROUTER AND SCREENS NAVIGATION STARTS HERE  */}
      {loading && <SplashScreen />}

      {!loading && (
        <Suspense>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route exact={true} path="/register" element={<SignUp />} />
            <Route path="/verify-phone" element={<VerifyPhone />} />
            <Route path="/validatepayment" element={<ValidatePayment />} />
            <Route path="/session-expired" element={<SessionExpired />} />
            <Route path="/validatepayment" element={<ValidatePayment />} />
            <Route path="/update-phone" element={<UpdatePhone />} />
            <Route path="/verify-email" element={<SignupOtp />} />
            <Route path="/public-cliq" element={<PublicCliqs />} />
            <Route path="/link" element={<Apps />} />
            <Route path="/link/register" element={<RegisterDeepLink />} />
            <Route path="/link/cliq" element={<CliqDeepLink />} />
            <Route path="/link/:slug" element={<MarketingDeepLink />} />
            <Route path="/register-cliq" element={<RegisterCliq />} />
            <Route path="/verify-email-v2" element={<VerifyEmailV2 />} />
            <Route path="/new-device" element={<NewDeviceDetected />} />
            <Route path="/set-pin" element={<SetTransactionPin />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/change-password" element={<ResetPassword2 />} />
            <Route path="/onboarding" element={<LoginModal />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/BVNSuccessModal" element={<BVNSuccessModal />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/CreatePlan" element={<CreatePlan />} />
            <Route path="/PlanSummary" element={<PlanSummary />} />
            <Route path="/PlansPayment" element={<PlansPayment />} />
            <Route path="/PlansSuccess" element={<PlansSuccess />} />
            <Route path="/personal-savings" element={<PersonalSavings />} />
            <Route path="/purse" element={<Purse />} />
            <Route path="/Investments" element={<Investments />} />
            <Route path="/agro" element={<Agro />} />
            <Route path="/dollar-investments" element={<Dollar />} />
            <Route path="/women-in-trade" element={<MyWIT />} />
            <Route path="/agro-investments" element={<MyAgroInvestments />} />
            <Route path="/new-wit" element={<NewWIT />} />
            <Route path="/bank" element={<Bank />} />
            <Route path="/cards" element={<Cards />} />
            <Route path="/referral" element={<Referral />} />
            <Route path="/invites" element={<Invites />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/about-herVest" element={<AboutHerVest />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/nok" element={<NOK />} />
            <Route path="/bvn" element={<BVNDetails />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/locked-savings" element={<Locked />} />
            <Route path="/airtime" element={<AirtimeandBills />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/support" element={<Support />} />
            <Route path="/cliq-savings" element={<Cliq />} />
            <Route path="update-password" element={<UpdatePassword />} />
            <Route path="loan" element={<Loan />} />
            <Route path="topupvalidate" element={<TopupValidate />} />
            <Route path="verify-mandate" element={<VerifyLoanMandate />} />
            <Route path="login-pin" element={<LoginPin />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      )}

      {/* ROUTER AND SCREEN NAVIGATION ENDS HERE */}

      {/* REACT TOASTER IMPLETENTATION STARTS HERE  */}
      <Toaster
        toastOptions={{
          success: {
            style: {
              background: "#5B2E4F",
              color: "white",
            },
          },
          error: {
            style: {
              background: "#5B2E4F",
              color: "white",
            },
          },
        }}
      />
      {/* REACT TOASTER IMPLETENTATION ENDS HERE   */}
    </>
  );
}

export default App;
